import { Injectable, inject } from '@angular/core';
import { Auth, GoogleAuthProvider, User, authState, signInWithPopup, signOut } from '@angular/fire/auth';
import { createServiceLogger } from '../utils/logger.util';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom, from, map, switchMap, tap, retry } from 'rxjs';
import { resetAnalyticsUser, setAnalyticsUser, trackAnalyticsEvent } from '../shared/analytics';

export type UserData = {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  lastLoginAt?: string;
  createdAt?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private router = inject(Router);
  private logger = createServiceLogger('AuthenticationService');

  private userSubject = new BehaviorSubject<User | null>(null);
  private authStateReady = false;

  readonly user$ = authState(this.auth).pipe(
    tap((user) => {
      this.userSubject.next(user);
      this.authStateReady = true;
    })
  );

  readonly isLoggedIn$ = this.user$.pipe(map((user) => !!user?.emailVerified));

  constructor() {
    // Initialize the auth state
    this.user$.subscribe();
  }

  async waitForAuthReady(): Promise<void> {
    if (this.authStateReady) return;
    await firstValueFrom(this.user$);
  }

  get currentUser(): User | null {
    if (!this.authStateReady) {
      this.logger.warn('Auth state not ready when accessing currentUser', {
        operation: 'get_current_user',
        metadata: { authStateReady: this.authStateReady }
      });
    }
    return this.userSubject.value;
  }

  get isLoggedIn(): boolean {
    const user = this.currentUser;
    return !!user?.emailVerified;
  }

  async authWithGoogle(): Promise<void> {
    const operationId = this.logger.startOperation('google_auth');
    trackAnalyticsEvent('auth_sign_in_google_started');
    
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      const credential = await signInWithPopup(this.auth, provider);

      if (!credential.user.emailVerified) {
        throw new Error('Email not verified');
      }

      await this.setUserData(credential.user);
      setAnalyticsUser({ uid: credential.user.uid });
      trackAnalyticsEvent('auth_sign_in_succeeded', {
        provider: 'google',
      });
      await this.router.navigate(['retivut']);
      
      this.logger.endOperation(operationId, { 
        success: true,
        userId: credential.user.uid,
        email: credential.user.email 
      });
    } catch (error) {
      this.logger.error('Google authentication failed', {
        operation: 'google_auth_failed'
      }, error);
      
      this.logger.endOperation(operationId, { 
        success: false,
        error: error.message 
      });
      // Re-throw specific error types for better error handling
      if (error instanceof Error) {
        throw new Error(`Authentication failed: ${error.message}`);
      }
      throw new Error('Authentication failed');
    }
  }

  private async setUserData(user: User): Promise<void> {
    if (!user.uid) {
      throw new Error('User ID is required');
    }

    const userRef = doc(this.firestore, `users/${user.uid}`);
    const userData: UserData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      lastLoginAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };

    try {
      await setDoc(userRef, userData, { merge: true });
    } catch (error) {
      this.logger.error('Error saving user data to Firestore', {
        operation: 'save_user_data_failed',
        userId: user.uid
      }, error);
      // Retry once after a short delay
      await new Promise((resolve) => setTimeout(resolve, 1000));
      try {
        await setDoc(userRef, userData, { merge: true });
      } catch (retryError) {
        this.logger.error('Final attempt to save user data failed', {
        operation: 'save_user_data_final_failure',
        userId: user.uid
      }, retryError);
        throw new Error('Failed to save user data after retry');
      }
    }

    // Claim sysadmin-issued pending invites for this email — mirrors the
    // Backoffice claim flow so invited users can onboard via Angular too.
    if (user.email) {
      try {
        await this.claimPendingInvites(user.uid, user.email);
      } catch (err) {
        this.logger.warn('Pending invite claim failed', {
          operation: 'claim_pending_invites_failed',
          userId: user.uid,
          metadata: { error: err instanceof Error ? err.message : String(err) },
        });
      }
    }
  }

  private async claimPendingInvites(uid: string, email: string): Promise<void> {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return;

    const invitesCol = collection(this.firestore, 'pendingInvites');
    const invitesSnap = await getDocs(
      query(invitesCol, where('email', '==', normalized), where('claimedAt', '==', null)),
    );
    if (invitesSnap.empty) return;

    const userRef = doc(this.firestore, `users/${uid}`);
    const userSnap = await getDoc(userRef);
    const userData = (userSnap.data() ?? {}) as {
      companies?: Record<string, unknown>;
    };

    const updates: Record<string, unknown> = {};

    invitesSnap.forEach((inviteDoc) => {
      const invite = inviteDoc.data() as {
        companyId?: string;
        role?: string;
      };
      const companyId = invite.companyId ?? 'default';
      if (!userData.companies?.[companyId]) {
        updates[`companies.${companyId}`] = {
          role: invite.role ?? 'editor',
          joinedAt: serverTimestamp(),
        };
      }
    });

    if (Object.keys(updates).length > 0) {
      updates['updatedAt'] = serverTimestamp();
      await updateDoc(userRef, updates);
    }

    await Promise.all(
      invitesSnap.docs.map((inviteDoc) =>
        updateDoc(inviteDoc.ref, {
          claimedAt: serverTimestamp(),
          claimedByUid: uid,
        }),
      ),
    );
  }

  async signOut(): Promise<void> {
    try {
      trackAnalyticsEvent('auth_logout_started');
      await signOut(this.auth);
      this.userSubject.next(null);
      resetAnalyticsUser();
      await this.router.navigate(['login']);
    } catch (error) {
      this.logger.error('Sign out failed', {
        operation: 'sign_out_failed'
      }, error);
      throw new Error('Failed to sign out');
    }
  }
}
